import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Download, Search as SearchIcon, Users, ClipboardList } from "lucide-react";
import { downloadCSV } from "@/lib/csv";
import { AdminTabs } from "@/components/AdminTabs";
import { FilterPills } from "@/components/FilterPills";
import { DateRangeFilter } from "@/components/DateRangeFilter";

const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");

const ROLE_OPTIONS = [
  { key: "all", label: "All" },
  { key: "customer", label: "Customers" },
  { key: "collector", label: "Collectors" },
  { key: "company", label: "Companies" },
];

export default function AdminConsentLog() {
  const [data, setData] = useState({ users: [], service_requests: [] });
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (role !== "all") params.role = role;
      if (range.start) params.start = range.start;
      if (range.end) params.end = range.end;
      const { data } = await api.get("/admin/agreements", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Reload whenever the role or date range changes (also handles initial mount).
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, range]);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const exportAccounts = () => {
    const rows = [];
    data.users.forEach((u) =>
      (u.agreements || []).forEach((a) =>
        rows.push({
          account: u.username,
          name: u.name,
          email: u.email,
          role: u.role,
          agreement: a.title,
          version: a.version,
          accepted_at: a.accepted_at,
          ip: a.ip || "",
        })
      )
    );
    if (!rows.length) return toast.info("No account records to export.");
    downloadCSV("account-agreements.csv", rows);
  };

  const exportRequests = () => {
    const rows = data.service_requests.map((s) => ({
      request_id: s.id,
      name: s.name,
      email: s.email,
      phone: s.phone,
      services: (s.services || []).join("; "),
      agreement: s.acknowledgment?.title,
      version: s.acknowledgment?.version,
      accepted_at: s.acknowledgment?.accepted_at,
      ip: s.acknowledgment?.ip || "",
    }));
    if (!rows.length) return toast.info("No request records to export.");
    downloadCSV("request-acknowledgments.csv", rows);
  };

  return (
    <div className="container-x py-12" data-testid="admin-consent-log">
      <AdminTabs />
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-2">Admin</div>
      <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2">
        <ShieldCheck className="w-6 h-6 text-[#2D6A4F]" /> Consent log
      </h1>
      <p className="text-[#5C6B64] mt-2 max-w-2xl">
        Auditable record of every agreement accepted on the platform — who accepted it, which version, when, and from what IP. Filter by account type and date range, then export to CSV for disputes or recordkeeping.
      </p>

      <div className="mt-6 space-y-4">
        <form onSubmit={onSearch} className="flex items-center gap-2 max-w-md" data-testid="admin-search-form">
          <input
            data-testid="admin-search-input"
            className="input-soft"
            placeholder="Search by name, email, username, or phone…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button type="submit" data-testid="admin-search-button" className="btn-primary py-2.5 px-4 inline-flex items-center gap-2">
            <SearchIcon className="w-4 h-4" /> Search
          </button>
        </form>

        <div className="flex flex-col gap-3 rounded-xl border border-[#1E3F33]/10 bg-[#F7F6F2] p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6" data-testid="admin-consent-filters">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Account type</span>
            <FilterPills options={ROLE_OPTIONS} value={role} onChange={setRole} testidPrefix="consent-role" />
          </div>
          <DateRangeFilter onChange={(start, end) => setRange({ start, end })} />
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-[#5C6B64]" data-testid="admin-loading">Loading…</div>
      ) : (
        <div className="mt-8 space-y-10">
          <section data-testid="admin-accounts-section">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h2 className="font-heading text-xl text-[#1E3F33] flex items-center gap-2">
                <Users className="w-5 h-5 text-[#CC5A3A]" /> Account agreements ({data.users.length})
              </h2>
              <button type="button" data-testid="admin-export-accounts" onClick={exportAccounts} className="btn-secondary text-xs py-2 inline-flex items-center gap-2">
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            </div>
            {data.users.length === 0 ? (
              <p className="text-sm text-[#8A9A86]">No account agreement records found.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-[#1E3F33]/10">
                <table className="w-full text-sm" data-testid="admin-accounts-table">
                  <thead className="bg-[#F0EFEA] text-[#1E3F33] text-left">
                    <tr>
                      <th className="px-4 py-2.5 font-medium">Account</th>
                      <th className="px-4 py-2.5 font-medium">Role</th>
                      <th className="px-4 py-2.5 font-medium">Agreement</th>
                      <th className="px-4 py-2.5 font-medium">Version</th>
                      <th className="px-4 py-2.5 font-medium">Accepted</th>
                      <th className="px-4 py-2.5 font-medium">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.flatMap((u) =>
                      (u.agreements || []).map((a, i) => (
                        <tr key={`${u.id}-${i}`} className="border-t border-[#1E3F33]/8" data-testid={`admin-account-row-${u.id}-${i}`}>
                          <td className="px-4 py-2.5">
                            <div className="font-medium text-[#1E3F33]">{u.name || u.username}</div>
                            <div className="text-xs text-[#8A9A86]">{u.email || u.username}</div>
                          </td>
                          <td className="px-4 py-2.5 capitalize text-[#5C6B64]">{u.role}</td>
                          <td className="px-4 py-2.5 text-[#1E3F33]">{a.title}</td>
                          <td className="px-4 py-2.5 text-[#5C6B64]">{a.version}</td>
                          <td className="px-4 py-2.5 text-[#5C6B64]">{fmt(a.accepted_at)}</td>
                          <td className="px-4 py-2.5 text-[#8A9A86] text-xs">{a.ip || "—"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section data-testid="admin-requests-section">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h2 className="font-heading text-xl text-[#1E3F33] flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-[#CC5A3A]" /> Customer request acknowledgments ({data.service_requests.length})
              </h2>
              <button type="button" data-testid="admin-export-requests" onClick={exportRequests} className="btn-secondary text-xs py-2 inline-flex items-center gap-2">
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            </div>
            {data.service_requests.length === 0 ? (
              <p className="text-sm text-[#8A9A86]">No request acknowledgment records found.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-[#1E3F33]/10">
                <table className="w-full text-sm" data-testid="admin-requests-table">
                  <thead className="bg-[#F0EFEA] text-[#1E3F33] text-left">
                    <tr>
                      <th className="px-4 py-2.5 font-medium">Customer</th>
                      <th className="px-4 py-2.5 font-medium">Agreement</th>
                      <th className="px-4 py-2.5 font-medium">Version</th>
                      <th className="px-4 py-2.5 font-medium">Accepted</th>
                      <th className="px-4 py-2.5 font-medium">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.service_requests.map((s) => (
                      <tr key={s.id} className="border-t border-[#1E3F33]/8" data-testid={`admin-request-row-${s.id}`}>
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-[#1E3F33]">{s.name}</div>
                          <div className="text-xs text-[#8A9A86]">{s.email || s.phone}</div>
                        </td>
                        <td className="px-4 py-2.5 text-[#1E3F33]">{s.acknowledgment?.title}</td>
                        <td className="px-4 py-2.5 text-[#5C6B64]">{s.acknowledgment?.version}</td>
                        <td className="px-4 py-2.5 text-[#5C6B64]">{fmt(s.acknowledgment?.accepted_at)}</td>
                        <td className="px-4 py-2.5 text-[#8A9A86] text-xs">{s.acknowledgment?.ip || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
